import {
  startOfISOWeek,
  endOfISOWeek,
  format,
  eachDayOfInterval,
} from 'date-fns';
import { WorkorderLaborWithContext } from '@certhon/models';
import { parseWeekString } from '@/utils/date';

// Re-export commonly used date/time utilities for backward compatibility
export {
  getCurrentWeekString,
  formatWeekString,
  parseWeekString,
  getWeekBounds,
  getPreviousWeek,
  getNextWeek,
  getWeekTitle,
  getWeekDateRange,
  formatTime,
  formatHours,
} from '@/utils/date';

/**
 * Day with grouped labor entries
 */
export interface DayLabor {
  date: Date;
  dayName: string;
  dateFormatted: string;
  entries: WorkorderLaborWithContext[];
  totalHours: number;
}

/**
 * Group labor entries by day of the week
 */
export function groupLaborByDay(
  labor: WorkorderLaborWithContext[],
  weekString: string,
): DayLabor[] {
  const weekStart = parseWeekString(weekString);
  const weekEnd = endOfISOWeek(weekStart);
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  return days.map((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayEntries = labor.filter((entry) => {
      const entryDateStr = format(entry.date_start, 'yyyy-MM-dd');
      return entryDateStr === dateStr;
    });

    return {
      date,
      dayName: format(date, 'EEEE'),
      dateFormatted: format(date, 'd MMM'),
      entries: dayEntries.sort(
        (a, b) => a.date_start.getTime() - b.date_start.getTime(),
      ),
      totalHours: dayEntries.reduce(
        (sum, e) => sum + calculateLaborHours(e),
        0,
      ),
    };
  });
}

/**
 * Calculate hours for a labor entry (gross time, not subtracting breaks)
 */
export function calculateLaborHours(entry: WorkorderLaborWithContext): number {
  const start = entry.date_start.getTime();
  const stop = entry.date_stop.getTime();
  return Math.max(0, (stop - start) / (1000 * 60 * 60));
}
